/**
 * Tema do site: claro, escuro ou seguindo o sistema operacional.
 *
 * O que a pessoa escolhe fica no `localStorage` e é aplicado como a classe
 * `.dark` no `<html>`, que é o que as variáveis de cor do `index.css` esperam.
 * Quem nunca escolheu segue o sistema — e acompanha ao vivo se o sistema mudar
 * de aparência ao longo do dia.
 *
 * O `index.html` aplica a mesma regra antes do primeiro desenho, para a página
 * não piscar clara em quem escolheu o modo noturno.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Tema = 'claro' | 'escuro' | 'sistema';

/** Chave do `localStorage`. Também é lida pelo script do `index.html`. */
export const CHAVE_TEMA = 'miolix.tema';

export const ROTULO_TEMA: Record<Tema, string> = {
  claro: 'Claro',
  escuro: 'Escuro',
  sistema: 'Automático (segue o sistema)',
};

interface ContextoTema {
  /** O que a pessoa escolheu. */
  tema: Tema;
  /** O que está de fato na tela (o “sistema” resolve para claro ou escuro). */
  resolvido: 'claro' | 'escuro';
  definirTema: (tema: Tema) => void;
  /** Passa para o próximo modo: claro → escuro → automático → claro. */
  alternar: () => void;
}

const Contexto = createContext<ContextoTema | null>(null);

function temaGuardado(): Tema {
  try {
    const valor = window.localStorage.getItem(CHAVE_TEMA);
    return valor === 'claro' || valor === 'escuro' || valor === 'sistema' ? valor : 'sistema';
  } catch {
    // Modo privado do navegador pode barrar o localStorage: seguimos o sistema.
    return 'sistema';
  }
}

function sistemaPrefereEscuro(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ProvedorTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(temaGuardado);
  const [sistemaEscuro, setSistemaEscuro] = useState(sistemaPrefereEscuro);

  // O sistema pode trocar sozinho (agendamento noturno do aparelho).
  useEffect(() => {
    const consulta = window.matchMedia('(prefers-color-scheme: dark)');
    const ouvir = (evento: MediaQueryListEvent) => setSistemaEscuro(evento.matches);
    consulta.addEventListener('change', ouvir);
    return () => consulta.removeEventListener('change', ouvir);
  }, []);

  const resolvido = tema === 'sistema' ? (sistemaEscuro ? 'escuro' : 'claro') : tema;

  useEffect(() => {
    const raiz = document.documentElement;
    raiz.classList.toggle('dark', resolvido === 'escuro');
    // Faz os controles nativos (barra de rolagem, campos, seleção) seguirem o tema.
    raiz.style.colorScheme = resolvido;
  }, [resolvido]);

  const definirTema = useCallback((novo: Tema) => {
    setTema(novo);
    try {
      window.localStorage.setItem(CHAVE_TEMA, novo);
    } catch {
      // Sem localStorage o tema vale só nesta aba — não vale quebrar por isso.
    }
  }, []);

  const alternar = useCallback(() => {
    definirTema(resolvido === 'escuro' ? 'claro' : 'escuro');
  }, [definirTema, resolvido]);

  const valor = useMemo<ContextoTema>(
    () => ({ tema, resolvido, definirTema, alternar }),
    [tema, resolvido, definirTema, alternar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useTema(): ContextoTema {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useTema precisa estar dentro de <ProvedorTema>.');
  return contexto;
}
